import { SearchInput, SearchInputProps } from 'components/search/SearchInput';
import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { forwardRef, Ref, useEffect, useState } from 'react';

export const defaultDebounceMs = 200;

export type DebouncedSearchInputProps = Omit<SearchInputProps, 'value' | 'onChange'> &
    Readonly<{
        /**
         * The current filter text value.
         */
        value?: string;
        /**
         * Debounced callback for when filter text changes.
         */
        onChange: (filterText: string) => void;
        /**
         * Filter debounce value in milliseconds (defaults to `200`).
         */
        filterDebounceMs?: number;
    }>;

function DebouncedSearchInput(
    {
        value,
        onChange,
        filterDebounceMs = defaultDebounceMs,
        ...rest
    }: DebouncedSearchInputProps,
    ref: Ref<HTMLInputElement>
) {
    const [inputValue, setInputValue] = useState(value ?? '');

    const debouncedOnChange = useDebounceCallback(filterDebounceMs, onChange);

    const onInputChange = (text: string) => {
        setInputValue(text);
        debouncedOnChange(text);
    };

    useEffect(() => setInputValue(value ?? ''), [value]);

    return (
        <SearchInput
            ref={ref}
            value={inputValue}
            onChange={(event) => onInputChange(event.target.value)}
            autofocus
            {...rest}
        />
    );
}

const forwardRefDebouncedSearchInput = forwardRef(DebouncedSearchInput);
export { forwardRefDebouncedSearchInput as DebouncedSearchInput };
